// Import Packages - Plugins
import { createApp, ref, watch, onMounted, onBeforeUpdate, nextTick, computed } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, sameAs, minLength, helpers } from '@vuelidate/validators'
import { createI18n } from 'vue-i18n';
import { vMaska } from 'maska';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import VueProgressBar from "@aacassandra/vue3-progressbar";
import axios from 'axios';
import qs from 'qs';

// Import Components
import Login from '../components/Login.vue'
import FullLoader from '../components/FullLoader.vue'
import Wizard from '../components/Wizard.vue'
import TippyExt from '../components/TippyExt.vue';
import DateField from '../components/DateField.vue'

// Import Languages
import { languages } from '../../translations/index.js'

const messages = Object.assign(languages);
const siteLang = document.documentElement.lang;

const options = {
    color: "#ffcf2c",
    failedColor: "#874b4b",
    thickness: "5px",
};


document.addEventListener('DOMContentLoaded', () => {
    const i18n = new createI18n({
        legacy: false,
        locale: siteLang,
        messages
    });
    const app = createApp({
        setup() {
            // DATA:
            // loading
            const showPopup = ref(false);

            // redirection
            const activePanel = ref(0);
            const panelParent = ref('');
            const panelChild = ref('');

            // function
            const loggedIn = ref(false);
            const passwordFieldType = ref('password');

            // properties
            const mortgage_number = ref('');
            const first_name = ref('');
            const last_name = ref('');

            // model
            const Model = ref({
                MortgageNumber: 0,
                QuestionGroup: 1,
                FirstName: "",
                LastName: "",
                RegisterQuestionList: [{}]
            });

            const RegisterQuestionList = ref([{}]);
            const ErroMessage = ref('');
            const IsValid = ref(true);
            const Questions = ref('');
            const ShowRegistration = ref(false);
            const C = ref('');

            // registrations
            const email = ref('');
            const email_confirm = ref('');
            const password = ref('');
            const password_confirm = ref('');
            const security_questionList1 = ref([]);
            const security_questionList2 = ref([]);
            const security_questionList3 = ref([]);
            const security_question1 = ref('');
            const security_question2 = ref('');
            const security_question3 = ref('');
            const security_answer1 = ref('');
            const security_answer2 = ref('');
            const security_answer3 = ref('');
            const borrower_confirmation = ref(false);
            const terms = ref('');
            const borrower_registration_completion = ref(false);
            const pwdValidation = (value) => {
                if (value === 'undefined' || value === null || value === '') {
                    return true;
                }
                return /^(?!.{101})(?=.{8})(?=.*[^A-Za-z])(?=.*[A-Z])(?=.*[a-z]).*$/g.test(value);
            }
            const isChecked = (value) => value === true;

            // validation properties
            const personalFormValidations = ref(['mortgage_number']);
            const securityFormValidations = ref([]);

            // dateOps
            const dateOps = ref({});
            const lang = ref('');
            const placeHolder = ref('MM/DD/YYYY');

            // This function is called before the component is updated. 
            // It sets the language value and updates the placeholder if the language is French.
            onBeforeUpdate(() => {
                lang.value = siteLang;
                if (lang.value == 'fr') {
                    placeHolder.value = 'JJ/MM/AAAA'
                }
            })

            // This function is called after the component is mounted. 
            // It checks if the form is completed and updates the panel values accordingly.
            onMounted(() => {
                nextTick(() => { // Wait for the next DOM update cycle. 
                    const g = cs;
                    if (continueRegistration === 'True') {
                        logout();
                        panelParent.value = 'registration_form';
                        panelChild.value = 'security_profile';
                        activePanel.value = 2;
                        email.value = emailAddress;
                    } else if (secureProfile === 'True') {
                        getSecurityProfileQuestions();
                        loggedIn.value = true;
                        panelParent.value = 'registration_form';
                        panelChild.value = 'security_profile';
                        activePanel.value = 2;
                        email.value = emailAddress;
                    } else if (consent === 'True') {
                        panelParent.value = 'registration_form';
                        panelChild.value = 'terms';
                        activePanel.value = 5;
                        email.value = emailAddress;
                    } else if (g != '') {
                        logout();
                        C.value = g;
                        panelParent.value = 'registration_form';
                        panelChild.value = 'email_password'
                        activePanel.value = 1;
                    } else {
                        setTimeout(() => getSecurityQuestions(), 5);
                    }
                })
            })

            // VALIDATIONS

            // This computed property defines the validation rules for the form fields.
            const rules = computed(() => {
                return {
                    mortgage_number: {
                        required
                    },
                    first_name: {
                        required
                    },
                    last_name: {
                        required
                    },
                    email: {
                        email,
                        required
                    },
                    email_confirm: {
                        email,
                        sameAsEmail: sameAs(email)
                    },
                    password: {
                        minLength: minLength(8),
                        required,
                        pwdValidation
                    },
                    password_confirm: {
                        sameAsPassword: sameAs(password)
                    },
                    security_question1: {
                        required
                    },
                    security_question2: {
                        required
                    },
                    security_question3: {
                        required
                    },
                    security_answer1: {
                        required,
                        minLength: minLength(6)
                    },
                    security_answer2: {
                        required,
                        minLength: minLength(6)
                    },
                    security_answer3: {
                        required,
                        minLength: minLength(6)
                    },
                    terms: {
                        required,
                        isChecked
                    },
                    borrower_confirmation: {
                        required,
                        isChecked
                    },
                    borrower_registration_completion: {
                        required,
                        isChecked
                    },
                    RegisterQuestionList: {
                        $each: helpers.forEach({
                            QuestionAnswer: { required } // Each question answer in the RegisterQuestionList is required.
                        })
                    },
                    $validationGroups: {
                        formPersonalInfo: ['mortgage_number', 'RegisterQuestionList', 'borrower_confirmation'],
                        formSecuriyVerification: ['RegisterQuestionList'],
                        formEmailPassword: ['email', 'email_confirm', 'password', 'password_confirm'],
                        formSecurityQuestions: ['security_question1', 'security_question2', 'security_question3', 'security_answer1', 'security_answer2', 'security_answer3'],
                        formTerms: ['terms', 'borrower_registration_completion'],
                        formTermsStrive: ['terms'],
                        personalInfoForm: ['borrower_confirmation'],
                    }
                }
            })

            // Use the Vuelidate library to apply the validation rules to the form fields.
            const v$ = useVuelidate(rules,
                {
                    mortgage_number,
                    RegisterQuestionList,
                    email,
                    email_confirm,
                    password,
                    password_confirm,
                    security_question1,
                    security_question2,
                    security_question3,
                    security_answer1,
                    security_answer2,
                    security_answer3,
                    terms,
                    borrower_confirmation,
                    borrower_registration_completion

                });

            // METHOD:

            // This computed property checks if any of the first three questions in the RegisterQuestionList are invalid.
            const isPersonalFormInvalid = computed(() => {
                return v$.value.RegisterQuestionList.$each.$response.$data.slice(0, 3).some(item => item.QuestionAnswer.$invalid);
            });

            // This computed property checks if morgate_number is invalid.
            const isMortgageNumberValid = computed(() => {
                return v$.value.mortgage_number.$invalid;
            });

            // This computed property checks if any of the next three questions in the RegisterQuestionList are invalid.
            const isSecurityFormInvalid = computed(() => {
                return v$.value.RegisterQuestionList.$each.$response.$data.slice(3, 6).some(item => item.QuestionAnswer.$invalid);
            });

            const isBorrowerConfirmed = computed(() => {
                return v$.value.borrower_confirmation.$invalid
            });

            const retry = () => {
                IsValid.value = true;
                panelChild.value = 'personal_information';
            }

            const getSecurityQuestions = () => {
                showPopup.value = true;

                var url = '/Account/GetSecurityQuestions?questionGroup=';

                axios.get(url + Model.value.QuestionGroup)
                    .then((response) => {
                        Model.value = response.data.Model;
                        RegisterQuestionList.value = Model.value.RegisterQuestionList;
                        Questions.value = response.data.Questions;
                        ShowRegistration.value = response.data.ShowRegistration;
                        showPopup.value = false;
                    })
                    .catch(e => {
                        showPopup.value = false;
                    })
            }

            const validateSecurityQuestions = () => {
                showPopup.value = true;
                var url = '/Account/RegisterNewMortgage/';

                var RegisterQuestionListArray = [];

                for (var i = 0; i < RegisterQuestionList.value.length; i++) {
                    const item = JSON.parse(JSON.stringify(RegisterQuestionList.value[i]));
                    item.SourceData = [];
                    RegisterQuestionListArray.push(item);
                }

                let token = document.getElementsByName('__RequestVerificationToken')[0].value;

                var model = {
                    "MortgageNumber": mortgage_number.value,
                    "QuestionGroup": Model.value.QuestionGroup,
                    "FirstName": "",
                    "LastName": "",
                    "RegisterQuestionList": RegisterQuestionListArray
                }

                var thetoken = "__RequestVerificationToken=" + token + '&';
                var str = qs.stringify(model);

                axios.post(url, thetoken + str)
                    .then((response) => {
                        Model.value = response.data.Model;
                        RegisterQuestionList.value = response.data.Model.RegisterQuestionList;
                        ErroMessage.value = response.data.ErroMessage;
                        IsValid.value = response.data.IsValid;
                        Questions.value = response.data.Questions;
                        ShowRegistration.value = response.data.ShowRegistration;
                        mortgage_number.value = null;
                        dateOps.value = { defaultDate: new Date() };
                        window.scrollTo(0, 1);

                        if (ShowRegistration.value) {
                            panelChild.value = 'email_password'
                            activePanel.value = 1;
                            C.value = response.data.C;
                        }
                        else {
                            getSecurityQuestions();
                            panelChild.value = 'show_both_panels';
                        }
                        showPopup.value = false;
                    })
                    .catch((error) => {
                        showPopup.value = false;
                    });
            }

            const updateValue = (value) => {
                const result = value.toString()
                mortgage_number.value = result.replace(new RegExp('\\D+', 'gi'), '')
            }

            const base64EncodeUnicode = (str) => {
                // First we escape the string using encodeURIComponent to get the UTF-8
                //encoding of the characters, 
                // then we convert the percent encodings into raw bytes,
                //and finally feed it to btoa() function.
                var utf8Bytes = encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => {
                    return String.fromCharCode('0x' + p1);
                });

                return btoa(utf8Bytes);
            }

            const validateUser = () => {
                showPopup.value = true;

                var url = '/Account/RegisterNewUser/';

                var model = {
                    "EmailAddress1": email.value,
                    "EmailAddress2": email_confirm.value,
                    "Password1": password.value,
                    "Password2": password_confirm.value,
                    "FirstName": "",
                    "LastName": "",
                    "ActivityGuid": C.value
                };

                model.Password1 = encodeURIComponent(model.Password1);
                model.Password2 = encodeURIComponent(model.Password2);

                var qs = require('qs');
                let token = document.getElementsByName('__RequestVerificationToken')[0].value;
                var thetoken = "__RequestVerificationToken=" + token + '&';
                var str = qs.stringify(model);
                axios.post(url, thetoken + str)
                    .then((response) => {
                        ErroMessage.value = response.data.ErroMessage;
                        IsValid.value = response.data.IsValid;

                        if (IsValid.value) {
                            panelChild.value = 'email_password_confirm'
                            activePanel.value = 2;
                        }
                        else {
                            location.href = '#errors';
                        }
                        showPopup.value = false;
                    })
                    .catch((error) => {
                        showPopup.value = false;
                    });
            }

            const logout = () => {
                let token = document.getElementsByName('__RequestVerificationToken')[0].value;
                var thetoken = "__RequestVerificationToken=" + token;

                axios.post('/Account/LogOff', thetoken);
            }

            const getSecurityProfileQuestions = () => {
                showPopup.value = true;

                axios.get('/Account/SecurityProfile')
                    .then((response) => {
                        security_questionList1.value = response.data.Model.QuestionList[0].QuestionRows;
                        security_questionList2.value = response.data.Model.QuestionList[1].QuestionRows;
                        security_questionList3.value = response.data.Model.QuestionList[2].QuestionRows;
                        showPopup.value = false;
                    })
                    .catch(e => {
                        showPopup.value = false;
                    })
            }
            
            const setSecurityProfileQuestions = () => {

                var url = '/Account/SetupSecurityProfile/';

                var model = {
                    "Question1": security_question1.value,
                    "Question2": security_question2.value,
                    "Question3": security_question3.value,
                    "Answer1": security_answer1.value,
                    "Answer2": security_answer2.value,
                    "Answer3": security_answer3.value
                };

                showPopup.value = true;

                var qs = require('qs');
                let token = document.getElementsByName('__RequestVerificationToken')[0].value;
                var thetoken = "__RequestVerificationToken=" + token + '&';
                var str = qs.stringify(model);
                axios.post(url, thetoken + str)
                    .then((response) => {
                    if (skipConsent === 'True') {
                        location.href = '/';
                        } else {
                            panelChild.value = 'terms'
                            activePanel.value = 4

                            showPopup.value = false;
                    }
                    })
                    .catch((error) => {
                        showPopup.value = false;
                });
            }
            
            const saveElectronicConsent = () => {

                var url = '/Account/SaveElectronicConsent/';

                showPopup.value = true;

                let token = document.getElementsByName('__RequestVerificationToken')[0].value;
                var thetoken = "__RequestVerificationToken=" + token;
                axios.post(url, thetoken) 
                    .then((response) => {
                        if (skipLoginButton === 'True') {
                            location.href = '/';
                        } else {
                            panelChild.value = 'complete'
                            activePanel.value = 5;
                            setTimeout(() => { location.href = '/'; }, 3000);
                            showPopup.value = false;
                        }
                    })
                    .catch((error) => {
                        showPopup.value = false;
                    });
            }

            const showPassword = () => {
                passwordFieldType.value = (passwordFieldType.value === 'password') ? 'text' : 'password';
            }

            const login = (e) => {
                if (e === true) {
                    getSecurityProfileQuestions();
                }
                loggedIn.value = e;
            }

            const submitPersonalInfo = (e) => {
                panelChild.value = 'security_validation'
                var el = document.getElementsByClassName('top')[0];
                el.focus();
            }

            const submitSecurityVerify = (e) => {
                validateSecurityQuestions();
            }

            const reloadSecurityQuestions = (e) => {
                getSecurityQuestions();
                panelChild.value = 'personal_information';
                activePanel.value--;
            }

            const submitEmailPassword = (e) => {
                validateUser();
            }

            const submitSecurityQuestions = (e) => {
                panelChild.value = 'confirm'
                activePanel.value = 3
            }

            const submitConfirm = (e) => {
                setSecurityProfileQuestions();
            }

            const submitTerms = (e) => {
                saveElectronicConsent();
            }

            const validateAnswer = () => {
                if ((v$.value.security_answer1.$error && security_answer1.value != '' && security_answer1.value.length < 6)
                    || (v$.value.security_answer2.$error && security_answer2.value != '' && security_answer2.value.length < 6)
                    || (v$.value.security_answer3.$error && security_answer3.value != '' && security_answer3.value.length < 6)) {
                    return true;
                } else {
                    return false;
                }
            }

            const delayTouch = () => {
                v$.value.$reset();
                setTimeout(() => {
                    v$.value.$touch();
                }, 2000)
            }
            

            return {
                // DATA:
                v$,
                showPopup,
                activePanel,
                panelParent,
                panelChild,
                loggedIn,
                mortgage_number,
                first_name,
                last_name,
                Model,
                RegisterQuestionList,
                ErroMessage,
                IsValid,
                Questions,
                ShowRegistration,
                C,
                passwordFieldType,
                email,
                email_confirm,
                password,
                password_confirm,
                security_questionList1,
                security_questionList2,
                security_questionList3,
                security_question1,
                security_question2,
                security_question3,
                security_answer1,
                security_answer2,
                security_answer3,
                borrower_confirmation,
                terms,
                borrower_registration_completion,
                personalFormValidations,
                securityFormValidations,
                dateOps,
                lang,
                placeHolder,
                // METHODS:
                retry,
                getSecurityQuestions,
                validateSecurityQuestions,
                updateValue,
                base64EncodeUnicode,
                validateUser,
                logout,
                getSecurityProfileQuestions,
                setSecurityProfileQuestions,
                saveElectronicConsent,
                showPassword,
                login,
                submitPersonalInfo,
                submitSecurityVerify,
                reloadSecurityQuestions,
                submitEmailPassword,
                submitSecurityQuestions,
                submitConfirm,
                submitTerms,
                validateAnswer,
                delayTouch,
                isPersonalFormInvalid,
                isSecurityFormInvalid,
                isBorrowerConfirmed,
                isMortgageNumberValid
            }
        }
    })

    app.use(i18n);
    app.use(VueProgressBar, options);
    app.directive('maska', vMaska);
    app.component('wizard', Wizard);
    app.component('login', Login);
    app.component('date-field', DateField);
    app.component('full-loader', FullLoader);
    app.component('tippy-ext', TippyExt);
    app.mount('#app');
}, false);
